import { useGetStationDisplayName } from "@/entities/station";
import {
    Routesoort,
    RoutesoortDisplay,
    TvvBesteldeRoute,
    TVVBestelStatus,
    useGetTvvBesteldeRoutes,
    Voertuigsoort,
    VoertuigsoortDisplay,
} from "@/entities/tvv";
import { TvvBus } from "@/entities/tvv/domain/tvv-bestelde-route.ts";
import { DossierSummaryCard, DossierSummaryCardSkeleton } from "@/features/dossier-details";
import { Dot, DotVariants, EmptyState, ErrorDisplay, Icon, Separated } from "@/shared/components";
import { formatDateTimeRelative } from "@/shared/utils";
import { getListPreview } from "@/shared/utils/list-preview.ts";

type TvvSummaryProps = {
    dossierNummer: string;
};

export function TvvSummary({ dossierNummer }: Readonly<TvvSummaryProps>) {
    const { data: routes, isPending, error } = useGetTvvBesteldeRoutes(dossierNummer);

    if (isPending) {
        return <DossierSummaryCardSkeleton section="Tvv" />;
    }

    if (error) {
        return <ErrorDisplay error={error} title="Fout bij het laden van TVV" />;
    }

    const status = routes.reduce(
        (total, route) => {
            const actieveBussen = TvvBesteldeRoute.actieveAndAfgeschaaldeBussen(route)[0].length;
            if (route.status !== TVVBestelStatus.Gestopt && route.status !== TVVBestelStatus.StopAangevraagd) {
                total.geleverd += actieveBussen;
                total.besteld += route.bussenBesteld;
            }
            return total;
        },
        { geleverd: 0, besteld: 0 }
    );

    const hasContent = routes?.length > 0;

    return (
        <DossierSummaryCard section="Tvv" status={status.besteld > 0 ? `${status.geleverd}/${status.besteld} bussen geleverd` : ""}>
            {hasContent ? (
                <TvvSummaryContent routes={routes} />
            ) : (
                <EmptyState className="pr-3 pl-4">Geen Treinvervangend vervoer toegevoegd</EmptyState>
            )}
        </DossierSummaryCard>
    );
}

type TvvSummaryContentProps = {
    routes: TvvBesteldeRoute[];
};

function TvvSummaryContent({ routes }: Readonly<TvvSummaryContentProps>) {
    const { previewItems: displayRoutes, remainder } = getListPreview(routes, 5);

    return (
        <>
            <Separated>
                {displayRoutes.map((route) => (
                    <TvvSummaryRoute key={`${route.dossierId}-${route.stationCodes.join("-")}`} route={route} />
                ))}
            </Separated>
            {remainder > 0 && <span className="text-s text-content-subtle">nog {remainder} routes</span>}
        </>
    );
}

type TvvSummaryRouteProps = {
    route: TvvBesteldeRoute;
};

function TvvSummaryRoute({ route }: Readonly<TvvSummaryRouteProps>) {
    const getStationDisplayName = useGetStationDisplayName();
    const actieveBussen = TvvBesteldeRoute.actieveAndAfgeschaaldeBussen(route)[0];
    let routeStatus: DotVariants["variant"] = "infoOpen";
    if (actieveBussen.length > 0) {
        routeStatus = "info";
    }
    if (
        TvvBesteldeRoute.isComplete(route) ||
        (TvvBesteldeRoute.isOVRoute(route.voertuigSoort) && route.status === TVVBestelStatus.Gedeeltelijk)
    ) {
        routeStatus = "succes";
    }
    if (route.status === TVVBestelStatus.Gestopt) {
        routeStatus = "neutral";
    }

    return (
        <div
            className="grid grid-cols-[60px_4px_1fr_0.3fr_0.35fr_24px] items-center gap-x-2 py-2 pr-3 pl-4"
            data-testid={`route-${route.stationCodes.join(">")}`}
        >
            <RouteVoertuigSoort routeSoort={route.routeSoort} voertuigSoort={route.voertuigSoort} />
            <Dot size="s" variant="emphasis" />
            <span className="text-content-default text-m">
                {getStationDisplayName(route.stationCodes.at(0)!)} - {getStationDisplayName(route.stationCodes.at(-1)!)}
            </span>
            <EtaDisplay firstBus={actieveBussen.at(0)} etasPubliek={route.etasPubliek} status={route.status} />
            <RouteStatus
                bussenGeleverd={actieveBussen.length}
                bussenBesteld={route.bussenBesteld}
                voertuigsoort={route.voertuigSoort}
                status={route.status}
            />
            <Dot className="justify-self-center" size="m" variant={routeStatus}></Dot>
        </div>
    );
}

type RouteStatusProps = {
    bussenGeleverd: number;
    bussenBesteld: number;
    status: TVVBestelStatus;
    voertuigsoort: Voertuigsoort;
};

function RouteStatus({ bussenBesteld, bussenGeleverd, voertuigsoort, status }: RouteStatusProps) {
    if (status === TVVBestelStatus.Gestopt) {
        return (
            <span className="text-m justify-self-end text-nowrap" data-testid="route-status">
                Gestopt
            </span>
        );
    }

    if (status === TVVBestelStatus.StopAangevraagd) {
        return (
            <span className="text-m justify-self-end text-nowrap" data-testid="route-status">
                Stop aangevraagd
            </span>
        );
    }

    if (TvvBesteldeRoute.isOVRoute(voertuigsoort)) {
        return (
            <span className="text-m justify-self-end text-nowrap" data-testid="route-status">
                Ingezet
            </span>
        );
    }

    return (
        <span className="text-m justify-self-end text-nowrap" data-testid="route-status">
            {bussenGeleverd}/{bussenBesteld} geleverd
        </span>
    );
}

function RouteVoertuigSoort(props: { routeSoort: Routesoort; voertuigSoort: Voertuigsoort }) {
    if (props.routeSoort === "Overig") {
        return <span className="text-content-emphasis text-m font-medium text-nowrap">{VoertuigsoortDisplay[props.voertuigSoort]}</span>;
    }
    return <span className="text-content-emphasis text-m font-medium text-nowrap">{RoutesoortDisplay[props.routeSoort]}</span>;
}

type EtaDisplayProps = {
    firstBus: TvvBus | undefined;
    etasPubliek: boolean;
    status: TVVBestelStatus;
};

function EtaDisplay({ firstBus, etasPubliek, status }: EtaDisplayProps) {
    return (
        <span className="text-content-subtle flex flex-row items-center gap-1 justify-self-start text-nowrap">
            {firstBus && status !== TVVBestelStatus.Gestopt && (
                <>
                    <Icon
                        name={etasPubliek ? "megaphone" : "megaphone-conflict"}
                        className={etasPubliek ? "text-content-default" : "text-content-subtle"}
                        size="xs"
                    />
                    1e ETA: {formatDateTimeRelative(firstBus.tijd)}
                </>
            )}
        </span>
    );
}
